import { useDispatch } from "react-redux";
import { useRootSelector } from "@/infrastructure/hooks";
import { BacktestAction, BacktestSel, BacktestType } from "@/features/backtest";
import { Button, Cascader, InputNumber, Modal, Popconfirm, Row, Select, Space, Tag, Tooltip, Typography } from "antd";
import styles from "@/modules/Backtest/Setup/Strategy/styles.less";
import { cloneDeep } from "lodash";
import React from "react";
import { DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons/lib";

type ValueOf<T> = T[keyof T];

export const useTableData = (isBuy: boolean) => {
  const dispatch = useDispatch();
  const availStrategyOptions = useRootSelector(BacktestSel.availStrategyOptions);
  const availCompareOptions = useRootSelector(BacktestSel.availCompareOptions);
  const strategy = useRootSelector(BacktestSel.strategy(isBuy));

  return {
    strategy,
    columns: [
      {
        title: "Condition",
        dataIndex: "and",
        key: "and",
        sorter: (a: BacktestType.StrategySetup, b: BacktestType.StrategySetup) => Number(a.and) - Number(b.and),
        render: (value: boolean, _record: any, index: number) => (
          <Row justify={"center"}>
            <Tag
              color={value ? "geekblue" : "purple"}
              className={styles.condition}
              onClick={() => updateCondition(index, "and")}
            >
              {value ? "And" : "Or"}
            </Tag>
          </Row>
        )
      },
      {
        title: "Indicator",
        dataIndex: "indicator1",
        key: "indicator1",
        width: 250,
        render: (indicator: string[], _record: any, index: number) => (
          <Cascader
            displayRender={e => e[1]}
            onChange={e => updateCondition(index, "indicator1", e as string[])}
            options={infoToolTip("indicator1")}
            expandTrigger="hover"
            style={{ width: "100%" }}
            value={indicator}
          />
        )
      },
      {
        title: "Param",
        dataIndex: "param1",
        key: "param1",
        render: (value: number, _record: any, index: number) => (
          <InputNumber
            onChange={e => updateCondition(index, "param1", e as number)}
            value={value}
            style={{ width: "100%" }}
            disabled={disableInput(index, "indicator1")}
          />
        )
      },
      {
        title: "Compare",
        dataIndex: "compare",
        key: "compare",
        width: 120,
        render: (e: string, _record: any, index: number) => (
          <Select
            value={e}
            style={{ width: "100%" }}
            onChange={e => updateCondition(index, "compare", e)}
            disabled={isStandalone(index)}
          >
            {availCompareOptions.map(c => (
              <Select.Option value={c} key={c}>
                {c}
              </Select.Option>
            ))}
          </Select>
        )
      },
      {
        title: "Indicator",
        dataIndex: "indicator2",
        key: "indicator2",
        width: 250,
        render: (indicator: string[], _record: any, index: number) => (
          <Cascader
            displayRender={e => e[1]}
            options={infoToolTip("indicator2")}
            onChange={e => updateCondition(index, "indicator2", e as string[])}
            expandTrigger="hover"
            style={{ width: "100%" }}
            value={indicator}
            disabled={isStandalone(index)}
          />
        )
      },
      {
        title: "Param",
        dataIndex: "param2",
        key: "param2",
        render: (value: number, _record: any, index: number) => (
          <InputNumber
            onChange={e => updateCondition(index, "param2", e as number)}
            value={value}
            style={{ width: "100%" }}
            disabled={isStandalone(index) || disableInput(index, "indicator2")}
          />
        )
      },
      {
        title: "",
        dataIndex: "buy",
        key: "buy",
        render: (_value: boolean, _record: any, index: number) => (
          <Row justify={"center"}>
            <Popconfirm
              key={"delete"}
              placement="left"
              title={`Are you sure to delete this strategy? This action cannot de undone.`}
              onConfirm={() => deleteStrategy(index)}
              okText="Yes"
              okType={"danger"}
              cancelText="No"
            >
              <Button danger icon={<DeleteOutlined />} shape={"round"} size={"small"} />
            </Popconfirm>
          </Row>
        )
      }
    ]
  };

  function updateCondition(
    index: number,
    type: keyof BacktestType.StrategySetup,
    value?: ValueOf<BacktestType.StrategySetup>
  ) {
    const newStrategy = cloneDeep(strategy);

    if (type === "and") {
      newStrategy[index][type] = !newStrategy[index][type];
    }

    if (value && (type === "indicator1" || type === "indicator2")) {
      const newValue = value as string[];
      if (isStandalone(index, newValue[0])) {
        newStrategy[index]["and"] = false;
      }
      newStrategy[index][type] = newValue;
    }

    if (value && (type === "param1" || type === "param2")) {
      newStrategy[index][type] = value as number;
    }

    if (value && type === "compare") {
      newStrategy[index][type] = value as string;
    }

    dispatch(BacktestAction.updateStrategy({ [isBuy ? "buy" : "sell"]: newStrategy }));
  }

  function infoToolTip(indicator: "indicator1" | "indicator2") {
    return indicator === "indicator2"
      ? _map(availStrategyOptions.filter(e => !e.standalone))
      : _map(availStrategyOptions);

    function _map(data: BacktestType.StrategyOptions[]) {
      return data.map(e => ({
        ...e,
        label: createTooltip(e.label, e.description),
        children: e.children.map(x => ({ ...x, label: createTooltip(x.label, x.description) }))
      }));
    }

    function createTooltip(label: string, description: string) {
      return (
        <Space size={6}>
          <Tooltip title={description} placement="right">
            <InfoCircleOutlined />
          </Tooltip>
          <Typography.Text>{label}</Typography.Text>
        </Space>
      );
    }
  }

  function disableInput(index: number, indicator: "indicator1" | "indicator2"): boolean {
    return availStrategyOptions
      .filter(e => !e.hasParam)
      .map(e => e.label)
      .includes(strategy[index][indicator][0]);
  }

  function isStandalone(index: number, selectedStrategy?: string): boolean {
    const standaloneStrategy = availStrategyOptions.filter(e => e.standalone).map(e => e.value);
    return selectedStrategy
      ? standaloneStrategy.includes(selectedStrategy)
      : standaloneStrategy.includes(strategy[index]["indicator1"][0]);
  }

  function deleteStrategy(index: number) {
    if (strategy.length === 1) {
      Modal.error({
        title: "Unable to delete this strategy",
        content: `At least one ${isBuy ? "buy" : "sell"} strategy is needed`
      });
      return;
    }

    const newStrategy = cloneDeep(strategy).filter((_e, i) => i !== index);

    dispatch(BacktestAction.updateStrategy({ [isBuy ? "buy" : "sell"]: newStrategy }));
  }
};
